import { createContext, useContext, useMemo } from 'react';
import { useOptionalFlowGraph, useOptionalNodes } from '@/components/Recipe/FlowContext';
import { useUserWorkflowRole, useWorkflowStore, type WorkflowRole } from '@/state/workflow.state';
import type { Node } from '@/types/node';

/**
 * The workflow a Community or org publish freezes. The editor reads it from its store and canvas;
 * the `/tool` page has neither, so its Share modal provides one built from the fetched workflow.
 */
export interface PublishWorkflowSource {
  id: string;
  name: string;
  role: WorkflowRole;
  /** The fetched draft's nodes, outside the editor only; the editor's readiness reads the canvas. */
  nodes?: Node[];
  /** The latest nodes, read when the author starts a publish. */
  getLatestNodes: () => Node[];
  /** Adopts the draft version the server forks after freezing the published one. */
  adoptDraftVersion: (version: number, updatedAt: string) => void;
}

const StandalonePublishWorkflowContext = createContext<PublishWorkflowSource | null>(null);

/** Supplies the workflow to publish where there is no editor (the `/tool` page). */
export const StandalonePublishWorkflowProvider = StandalonePublishWorkflowContext.Provider;

/** The workflow to publish: the one provided outside the editor, otherwise the editor's own. */
export function usePublishWorkflowSource(): PublishWorkflowSource {
  const standalone = useContext(StandalonePublishWorkflowContext);
  const id = useWorkflowStore((state) => state.recipe.id);
  const name = useWorkflowStore((state) => state.recipe.name);
  const role = useUserWorkflowRole();
  const graph = useOptionalFlowGraph();

  const editorSource = useMemo<PublishWorkflowSource>(
    () => ({
      id,
      name,
      role,
      getLatestNodes: () => (graph ? graph.store.getNodes() : []),
      adoptDraftVersion: (version, updatedAt) => {
        const { recipe: workflow, setRecipe } = useWorkflowStore.getState();
        setRecipe({ ...workflow, version, updatedAt });
      },
    }),
    [graph, id, name, role],
  );

  return standalone ?? editorSource;
}

/**
 * The nodes the readiness blockers check, re-rendering on canvas edits. Kept apart from
 * {@link usePublishWorkflowSource} so only readiness subscribes to every node change.
 */
export function usePublishWorkflowNodes(): Node[] {
  const standaloneNodes = useContext(StandalonePublishWorkflowContext)?.nodes;
  const canvasNodes = useOptionalNodes();
  return standaloneNodes ?? canvasNodes;
}
